import { useState, useEffect, useCallback, useRef } from 'react';
import { request } from '../services/api';
import { useI18n } from '../../../i18n';
import { NotificationPanel } from './NotificationPanel';

const BELL_PATH = 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0';

/**
 * Campana topbar de notificaciones.
 *
 * Desde 0656 (UX-2 cadena 0654): el badge cuenta dual-source para que el aprobador
 * vea TANTO las notificaciones M17 históricas como los pendientes vivos resolvidos
 * via puesto+rol (cadena `workflow_etapa_rol`), incluso si la transición original
 * no generó fila en `NTF.notificaciones` (deuda histórica pre-fix 0653).
 *
 *   badge = COUNT(/notifications/count · M17) + COUNT(/workflow/pendientes/count · multietapa)
 *
 * Patrón espejo del de `ActionCenterBadge.tsx` (que ya lo hace bien desde 0615+).
 */
export function NotificationBell() {
  const { t } = useI18n();
  const [count, setCount] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const authFailedRef = useRef(false);

  const fetchCount = useCallback(async () => {
    if (authFailedRef.current) return;
    try {
      const [notifs, pendientes] = await Promise.all([
        request<{ data: { count: number } }>('/notifications/count').catch(() => ({
          data: { count: 0 },
        })),
        request<{ data: { count: number } }>('/workflow/pendientes/count').catch(() => ({
          data: { count: 0 },
        })),
      ]);
      setCount((notifs.data?.count ?? 0) + (pendientes.data?.count ?? 0));
    } catch (err: unknown) {
      // Si 401, dejar de intentar — sesión no establecida
      if (err instanceof Error && err.message.includes('401')) {
        authFailedRef.current = true;
      }
    }
  }, []);

  useEffect(() => {
    authFailedRef.current = false;
    fetchCount();
    // Polling cada 30s — no hay cliente Supabase Realtime en frontend
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginRight: 8 }}>
      <button
        onClick={() => setPanelOpen((p) => !p)}
        title={t.topbar.notifications.tooltipBell}
        aria-label={t.topbar.notifications.tooltipBell}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 6,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.6)',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.1)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = 'transparent';
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={BELL_PATH} />
        </svg>
        {count > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              background: '#E53E3E',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              lineHeight: 1,
              fontFamily: 'inherit',
            }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {panelOpen && (
        <NotificationPanel onClose={() => setPanelOpen(false)} onCountChange={setCount} />
      )}
    </div>
  );
}
